var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo } from 'react';
import { dateFormat, dateFormatForUser } from '@/presentation/utils/parse-date';
import { useRecoilState } from 'recoil';
import { formatStringAccountBalance } from '@/presentation/utils/money-utils';
import useCustomToast from '@/presentation/hooks/use-custom-toast';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import useSystemAccount from '@/presentation/hooks/use-system-account';
const globalSpanTypeStyle = {
    padding: '5px 10px',
    borderRadius: '14px',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal'
};
const useTransactionTable = ({ manageVirtualTransactions, virtualAccountIds = [], mainTableState }) => {
    const [state, setState] = useRecoilState(mainTableState);
    const { showCustomError } = useCustomToast();
    const { isMainSystemChecking } = useSystemAccount();
    useEffect(() => {
        try {
            if (virtualAccountIds.length > 0 && virtualAccountIds[0])
                getTransactionsPaginatedLocal(state.filters);
        }
        catch (error) {
            showCustomError('Error', 'Error loading transactions');
        }
    }, [
        state.filters.deleted,
        state.filters.input,
        state.filters.selectedClassId,
        state.filters.selectedGradeId,
        state.reloadTransactions,
        virtualAccountIds.join(',')
    ]);
    const getTransactionsPaginatedLocal = (filters, pagination = {
        pageIndex: 1,
        pageSize: 10
    }, sort) => __awaiter(void 0, void 0, void 0, function* () {
        changeLoading(true);
        const filterProps = {
            input: filters.input
        };
        const records = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const result = yield manageVirtualTransactions.getTransactionsPaginated(filterProps, sort, pagination, {
                    virtualAccountId: virtualAccountIds
                });
                setState(prev => (Object.assign(Object.assign({}, prev), { transactions: {
                        data: (result === null || result === void 0 ? void 0 : result.data) || [],
                        count: (result === null || result === void 0 ? void 0 : result.count) || 0,
                        total: (result === null || result === void 0 ? void 0 : result.total) || 0,
                        page: (result === null || result === void 0 ? void 0 : result.page) || 0,
                        pageCount: (result === null || result === void 0 ? void 0 : result.pageCount) || 0
                    }, dataNotFound: false, isTableLoading: false })));
                return result;
            }
            catch (error) {
                showCustomError('Error', 'Error loading transactions');
            }
        });
        yield records();
    });
    const changeLoading = (value) => {
        setState(prev => {
            return Object.assign(Object.assign({}, prev), { isTableLoading: value });
        });
    };
    const columnAlternativeNames = {
        createdAt: 'Date',
        amount: 'Amount',
        type: 'Type',
        category: 'Category',
        description: 'Description'
    };
    const columnsWithoutSort = [];
    const setColumnsByModel = () => {
        const keys = ['createdAt', 'amount', 'type', 'category', 'description'];
        const columns = keys.map(key => {
            return Object.assign({ accessorFn: row => row[key], id: key, cell: info => info.getValue(), header: () => React.createElement("span", null, columnAlternativeNames[key]), enableSorting: true }, getColumnSize(key));
        });
        return columns;
    };
    const sizeProps = (size) => ({ size, maxSize: size, minSize: size });
    const getColumnSize = (key) => {
        switch (key) {
            case 'createdAt':
                return sizeProps(20);
            case 'amount':
                return sizeProps(10);
            case 'type':
                return sizeProps(10);
            case 'category':
                return sizeProps(25);
            case 'description':
                return sizeProps(35);
        }
    };
    const columns = useMemo(() => [...setColumnsByModel()], []);
    const renderCellByType = (context, row) => {
        const { column } = context;
        const mapAccountLabel = (account) => {
            var _a, _b;
            switch ((_a = account === null || account === void 0 ? void 0 : account.accountType) === null || _a === void 0 ? void 0 : _a.description) {
                case 'Checking':
                    return `Checking Account`;
                case 'Credit Card':
                    return `Credit Card`;
                case 'brokerage':
                case 'Brokerage':
                    return `Brokerage Account`;
                case 'Pocket':
                    return `Savings - ${(account === null || account === void 0 ? void 0 : account.savingName) || 'General account'}`;
                default:
                    if (isMainSystemChecking(account === null || account === void 0 ? void 0 : account.userId))
                        return 'System account';
                    return ((_b = account === null || account === void 0 ? void 0 : account.accountType) === null || _b === void 0 ? void 0 : _b.description) || 'Unnamed Account';
            }
        };
        const mapFrontCategory = (category) => {
            if (category === 'Utility')
                return 'Utilities';
            return category;
        };
        const mapTransferCategory = (row) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            if (isMainSystemChecking((_a = row === null || row === void 0 ? void 0 : row.accountTo) === null || _a === void 0 ? void 0 : _a.userId)) {
                if (((_b = row === null || row === void 0 ? void 0 : row.data) === null || _b === void 0 ? void 0 : _b.detail) === 'OVERDRAFT_STUDENT_FEE')
                    return 'Overdraft Fee';
                return mapFrontCategory((_d = (_c = row === null || row === void 0 ? void 0 : row.accountTo) === null || _c === void 0 ? void 0 : _c.accountType) === null || _d === void 0 ? void 0 : _d.description);
                // return 'Revert T.';
            }
            if ((_f = (_e = row.accountTo) === null || _e === void 0 ? void 0 : _e.savingCategory) === null || _f === void 0 ? void 0 : _f.description)
                return (_h = (_g = row.accountTo) === null || _g === void 0 ? void 0 : _g.savingCategory) === null || _h === void 0 ? void 0 : _h.description;
            if (isMainSystemChecking((_j = row === null || row === void 0 ? void 0 : row.accountFrom) === null || _j === void 0 ? void 0 : _j.userId))
                return 'Income';
            const accountToDescription = (_l = (_k = row === null || row === void 0 ? void 0 : row.accountTo) === null || _k === void 0 ? void 0 : _k.accountType) === null || _l === void 0 ? void 0 : _l.description;
            if (accountToDescription === 'Pocket')
                return 'Savings';
            if (accountToDescription === 'Credit Card')
                return 'Bill Payment';
            if ((accountToDescription === null || accountToDescription === void 0 ? void 0 : accountToDescription.toLocaleLowerCase()) === 'brokerage')
                return 'Investment';
            return 'General';
        };
        const mapTransactionType = (row) => {
            var _a, _b;
            if (isMainSystemChecking((_a = row === null || row === void 0 ? void 0 : row.accountFrom) === null || _a === void 0 ? void 0 : _a.userId)) {
                return (React.createElement("span", { "data-tooltip-id": `t-t-id-${row === null || row === void 0 ? void 0 : row.id}`, style: Object.assign({ backgroundColor: 'rgba(101, 199, 2, 0.1)', color: '#65c702' }, globalSpanTypeStyle) }, "Deposit"));
            }
            if (isMainSystemChecking((_b = row === null || row === void 0 ? void 0 : row.accountTo) === null || _b === void 0 ? void 0 : _b.userId)) {
                return (React.createElement("span", { "data-tooltip-id": `t-t-id-${row === null || row === void 0 ? void 0 : row.id}`, style: Object.assign({ backgroundColor: '#F04A4A1A', color: '#F04A4A' }, globalSpanTypeStyle) }, "Debit"));
            }
            return (React.createElement("span", { "data-tooltip-id": `t-t-id-${row === null || row === void 0 ? void 0 : row.id}`, style: Object.assign({ backgroundColor: 'rgba(153, 153, 153, 0.1)', color: 'black' }, globalSpanTypeStyle) }, "Transfer"));
        };
        const mapTransactionAmount = (row) => {
            var _a, _b;
            if (isMainSystemChecking((_a = row === null || row === void 0 ? void 0 : row.accountFrom) === null || _a === void 0 ? void 0 : _a.userId))
                return React.createElement("span", { style: { color: '#65c702' } },
                    "+",
                    formatStringAccountBalance(row === null || row === void 0 ? void 0 : row.amount),
                    " KV");
            if (isMainSystemChecking((_b = row === null || row === void 0 ? void 0 : row.accountTo) === null || _b === void 0 ? void 0 : _b.userId))
                return React.createElement("span", { style: { color: '#f04a4a' } },
                    "-",
                    formatStringAccountBalance(row === null || row === void 0 ? void 0 : row.amount),
                    " KV");
            return React.createElement("span", null,
                formatStringAccountBalance(row === null || row === void 0 ? void 0 : row.amount),
                " KV");
        };
        const mapDescription = (row) => {
            var _a, _b;
            if (isMainSystemChecking((_a = row === null || row === void 0 ? void 0 : row.accountTo) === null || _a === void 0 ? void 0 : _a.userId)) {
                return (React.createElement("span", null,
                    "From ",
                    React.createElement("b", null, mapAccountLabel(row === null || row === void 0 ? void 0 : row.accountFrom))));
            }
            if (((_b = row === null || row === void 0 ? void 0 : row.data) === null || _b === void 0 ? void 0 : _b.detail) === 'APPLY_BALANCE') {
                return (React.createElement("span", null,
                    React.createElement("b", null, "Bonus KV Bucks")));
            }
            return (React.createElement("span", null,
                "From ",
                React.createElement("b", null, mapAccountLabel(row === null || row === void 0 ? void 0 : row.accountFrom)),
                " to ",
                React.createElement("b", null, mapAccountLabel(row === null || row === void 0 ? void 0 : row.accountTo))));
        };
        switch (column.id) {
            case 'createdAt':
                return (React.createElement("div", null,
                    React.createElement("span", { "data-tooltip-id": `t-d-id-${row === null || row === void 0 ? void 0 : row.id}`, style: { backgroundColor: 'white' } }, dateFormat(row === null || row === void 0 ? void 0 : row.created_at)),
                    React.createElement(ReactTooltip, { id: `t-d-id-${row === null || row === void 0 ? void 0 : row.id}`, place: "top", content: `${dateFormatForUser(row === null || row === void 0 ? void 0 : row.created_at)}` })));
            case 'amount':
                return mapTransactionAmount(row);
            case 'category':
                return React.createElement("span", null, mapTransferCategory(row));
            case 'description':
                return mapDescription(row);
            case 'type':
                return (React.createElement(React.Fragment, null,
                    mapTransactionType(row),
                    React.createElement(ReactTooltip, { id: `t-t-id-${row === null || row === void 0 ? void 0 : row.id}`, place: "top", content: `Transaction: T-${row === null || row === void 0 ? void 0 : row.id}` })));
            default:
                return React.createElement("span", null);
        }
    };
    return {
        columns,
        columnsWithoutSort,
        getTransactionsPaginated: getTransactionsPaginatedLocal,
        renderCellByType
    };
};
export default useTransactionTable;
